import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivePlayer } from '../components/ActivePlayer'
import { CategoryNav, HOT_SLUG, type ViewMode } from '../components/CategoryNav'
import { IntroBackgroundVideo, type IntroBackgroundVideoHandle } from '../components/IntroBackgroundVideo'
import { MomentCard } from '../components/MomentCard'
import { useCategoryUrl, readUrlState, type FilterSlug } from '../hooks/useCategoryUrl'
import { useVisibilityTrigger } from '../hooks/useScrollPlayback'
import { api } from '../lib/api'
import type { User } from '../lib/firebase'
import { CATEGORIES as FALLBACK_CATEGORIES, MOMENTS as FALLBACK_MOMENTS } from '../mockData'
import { fetchYouTubeMeta, parseYouTubeUrl } from '../lib/parseYouTube'
import { buildShareUrl } from '../lib/shareUrl'
import type { Category, HomepageSettings, IntroVideo, MomentView } from '../types'

const UNMUTE_KEY = 'zs:unmuted'
const SHARED_MOMENT_ID = '__shared__'
const DEFAULT_INTRO: HomepageSettings = {
  intros: [{ youtubeId: 'C3VIxj-lXRQ', startSeconds: 397 }]
}

const CurationBoard = lazy(() =>
  import('../components/CurationBoard').then((m) => ({ default: m.CurationBoard }))
)
const MomentEditor = lazy(() =>
  import('../components/MomentEditor').then((m) => ({ default: m.MomentEditor }))
)
const IntroVideoEditor = lazy(() =>
  import('../components/IntroVideoEditor').then((m) => ({ default: m.IntroVideoEditor }))
)
const MapView = lazy(() =>
  import('../components/MapView').then((m) => ({ default: m.MapView }))
)
const ShareModal = lazy(() =>
  import('../components/ShareModal').then((m) => ({ default: m.ShareModal }))
)
const PlayerMode = lazy(() =>
  import('../components/PlayerMode').then((m) => ({ default: m.PlayerMode }))
)

// email 白名單（與 server 端應一致）— 可由 Firebase token 查，但前端僅用於 UI gate
const ADMIN_EMAILS = ['pin0513@gmail.com']

type EditorMode = null | { kind: 'new' } | { kind: 'edit'; moment: MomentView }

// 進站時就先讀 URL：若使用者深入連結（/<slug> 或 /map 或 ?m=<id>）→ 預設略過 intro
const INITIAL_URL = readUrlState()
const INITIAL_HAS_MOMENT =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('m')
// 音樂時光分享連結 /?play=<seed>&from=<point|start>
const INITIAL_PLAYER: { seed: number; fromStart: boolean } | null = (() => {
  if (typeof window === 'undefined') return null
  const p = new URLSearchParams(window.location.search)
  if (!p.has('play')) return null
  return { seed: Number(p.get('play')) || 0, fromStart: p.get('from') === 'start' }
})()
const IS_DEEP_LINK =
  INITIAL_URL.slug !== null || INITIAL_URL.mapOpen || INITIAL_HAS_MOMENT || INITIAL_PLAYER !== null

export function Home() {
  const [activeFilter, setActiveFilter] = useState<FilterSlug>(INITIAL_URL.slug)
  const [mapOpen, setMapOpen] = useState<boolean>(INITIAL_URL.mapOpen)
  // 「音樂時光」獨立播放器模式（全螢幕，取代 feed/nav，避免兩個 YT player 同時播）
  const [playerOpen, setPlayerOpen] = useState(!!INITIAL_PLAYER)
  // 分享連結帶來的播放器設定（seed/模式）；按鈕進入時清空 → 隨機 seed
  const [sharedPlayer, setSharedPlayer] = useState(INITIAL_PLAYER)
  // 橫向切換方向：1 = 往下一分類，-1 = 往上一分類，供 CSS 動畫使用
  const [slideDir, setSlideDir] = useState(0)
  const [slideNonce, setSlideNonce] = useState(0)
  // 一律從 muted=true 開始（瀏覽器 autoplay policy 強制），
  // 之前 unmute 過的 session → 第一次點擊/觸控恢復（見下方 useEffect）
  const [muted, setMuted] = useState<boolean>(true)
  // 深入連結（URL 帶分類或地圖）→ 直接進 feed，不再強迫看 intro
  const [immersed, setImmersed] = useState(IS_DEEP_LINK)
  const [categories, setCategories] = useState<Category[]>(FALLBACK_CATEGORIES)
  const [moments, setMoments] = useState<MomentView[]>(FALLBACK_MOMENTS)
  const [user, setUser] = useState<User | null>(null)
  const [editor, setEditor] = useState<EditorMode>(null)
  const [introEditorOpen, setIntroEditorOpen] = useState(false)
  const [homepageSettings, setHomepageSettings] = useState<HomepageSettings>(DEFAULT_INTRO)
  const [pickedIntro, setPickedIntro] = useState<IntroVideo | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('feed')
  const [reloadKey, setReloadKey] = useState(0)
  // 深入連結時直接拿掉 intro 整層（不只是把 immersed 設 true，連 overlay 都不渲染）
  const [introVisible, setIntroVisible] = useState(!IS_DEEP_LINK)
  const [introExiting, setIntroExiting] = useState(false)
  // 觸發 ActivePlayer seek 回當前 moment startSeconds 的 nonce（unmute 時 bump）
  const [replayNonce, setReplayNonce] = useState(0)
  // 從 map 點卡片 → 暫存「想跳到哪張」；等 visibleMoments 更新後 scrollIntoView + 設 active
  const [pendingPickId, setPendingPickId] = useState<string | null>(null)
  // header player 的 ▶/⏸ 與分享 modal 狀態
  const [paused, setPaused] = useState(false)
  // 要分享哪一個 moment（null = 沒開 modal）— navigator.share 不支援時才會用到
  const [shareMoment, setShareMoment] = useState<MomentView | null>(null)
  // 區分「從 intro 滑進來」vs「深層連結直接進站」：只有前者才壓制進場 scroll 慣性，
  // 否則會把深層連結的 scrollIntoView 壓回頂端（見壓制慣性 effect）。
  const enteredViaIntroRef = useRef(false)
  // 深層連結 ?m=<id> 只消費一次
  const deepLinkConsumed = useRef(false)

  const canEdit = !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())

  useEffect(() => {
    let unsub: (() => void) | undefined
    let cancelled = false
    const loadAuth = () => import('../lib/firebase').then(({ onUser }) => {
      if (cancelled) return
      unsub = onUser(setUser)
    })
    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const idleId =
      idleWindow.requestIdleCallback?.(loadAuth, { timeout: 3000 }) ??
      window.setTimeout(loadAuth, 1500)
    return () => {
      cancelled = true
      if (idleWindow.cancelIdleCallback) idleWindow.cancelIdleCallback(idleId)
      else window.clearTimeout(idleId)
      unsub?.()
    }
  }, [])

  // 首頁背景影片設定 — server 回傳 intros[] 候選池
  useEffect(() => {
    api
      .getHomepageSettings()
      .then((r) => setHomepageSettings(r.data))
      .catch(() => setHomepageSettings(DEFAULT_INTRO))
  }, [reloadKey])

  // 每次 settings 變動 → 從池裡隨機挑一支當這次進站的背景
  useEffect(() => {
    const list = homepageSettings.intros
    if (!list || list.length === 0) {
      setPickedIntro(null)
      return
    }
    setPickedIntro(list[Math.floor(Math.random() * list.length)])
  }, [homepageSettings])

  useEffect(() => {
    let cancelled = false
    // 分開 await — 其中一個失敗也不影響另一個（避免 Promise.all reject 連累 categories）
    api
      .listCategories()
      .then((cats) => {
        if (!cancelled && cats.data.length > 0) setCategories(cats.data)
      })
      .catch(() => {
        /* keep mock */
      })
    api
      .listMoments()
      .then((moms) => {
        if (!cancelled && moms.data.length > 0) setMoments(moms.data)
      })
      .catch(() => {
        /* keep mock */
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  // 分享連結（?yt=... 或 ?share=...）
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const share = params.get('share')
    const ytId = params.get('yt')
    const categorySlug = params.get('category')

    let parsed: { youtubeId: string; startSeconds: number } | null = null
    if (share) parsed = parseYouTubeUrl(share)
    else if (ytId) {
      parsed = { youtubeId: ytId, startSeconds: Number(params.get('t') ?? '0') || 0 }
    }
    if (!parsed) return

    let cancelled = false
    fetchYouTubeMeta(parsed.youtubeId).then((meta) => {
      if (cancelled) return
      const chosenCategory =
        categories.find((c) => c.slug === categorySlug) ??
        categories.find((c) => c.slug === 'ost') ??
        categories[0]
      if (!chosenCategory) return
      const now = new Date().toISOString()
      const synthetic: MomentView = {
        id: SHARED_MOMENT_ID,
        videoId: SHARED_MOMENT_ID,
        youtubeId: parsed!.youtubeId,
        title: meta.title || '分享的片刻',
        note: '從 YouTube 分享連結即時產生的片刻 — 分類：' + chosenCategory.name,
        startSeconds: parsed!.startSeconds,
        categoryId: chosenCategory.id,
        tags: ['shared'],
        heat: 0,
        curatedOrder: -1,
        createdAt: now,
        updatedAt: now,
        category: chosenCategory,
        video: {
          id: SHARED_MOMENT_ID,
          youtubeId: parsed!.youtubeId,
          originalTitle: meta.title || ''
        }
      }
      setMoments((prev) => [synthetic, ...prev.filter((m) => m.id !== SHARED_MOMENT_ID)])
      setImmersed(true)
    })
    return () => {
      cancelled = true
    }
  }, [categories])

  const visibleMoments = useMemo(() => {
    // 🔥 吸睛度：只顯示策展勾選 (featured=true) 的，依 featuredOrder 排序
    if (activeFilter === HOT_SLUG) {
      return moments
        .filter((m) => m.featured)
        .sort(
          (a, b) =>
            (a.featuredOrder ?? 999) - (b.featuredOrder ?? 999) ||
            a.curatedOrder - b.curatedOrder
        )
    }
    // 全部：先依分類 sortOrder 分組 → 分類內依吸睛度（viewCount） → dwellMs → createdAt desc
    // 效果：從 OST 這區開始看，每區最吸睛的排前面；看完一區接下一區
    if (activeFilter === null) {
      return [...moments].sort((a, b) => {
        const catDiff = (a.category?.sortOrder ?? 999) - (b.category?.sortOrder ?? 999)
        if (catDiff !== 0) return catDiff
        return (
          (b.viewCount ?? 0) - (a.viewCount ?? 0) ||
          (b.dwellMs ?? 0) - (a.dwellMs ?? 0) ||
          (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
        )
      })
    }
    // 個別分類：依策展順序（小的先），熱度破同分
    const list = moments.filter((m) => m.category.slug === activeFilter)
    return [...list].sort((a, b) => a.curatedOrder - b.curatedOrder || b.heat - a.heat)
  }, [activeFilter, moments])

  // ── 播放矩陣核心 state：哪一個 moment 正在播 ──
  // 所有 trigger（scroll IO / 橫滑 / 鍵盤 / visibleMoments 校正）都走這個入口，
  // 不再依賴「誰在畫面上 render 了 YouTubePlayer」。
  const [activeMomentId, setActiveMomentId] = useState<string | null>(null)

  // scroll 觀察 → 被動觸發 setActiveMomentId
  const registerItem = useVisibilityTrigger(
    useCallback((id: string) => setActiveMomentId(id), [])
  )

  // visibleMoments 變化（切分類、載入完成、分享連結插入）時校正 activeMomentId
  useEffect(() => {
    if (visibleMoments.length === 0) {
      setActiveMomentId(null)
      return
    }
    if (!activeMomentId || !visibleMoments.some((m) => m.id === activeMomentId)) {
      // 有 pending 深層連結 / 地圖跳卡時優先鎖定它，避免冷啟動 mock→real 換卡時
      // 先閃到第一張，被 IntersectionObserver 鎖死成第一張歌。
      const pinned =
        pendingPickId && visibleMoments.some((m) => m.id === pendingPickId)
          ? pendingPickId
          : visibleMoments[0].id
      setActiveMomentId(pinned)
    }
  }, [visibleMoments, activeMomentId, pendingPickId])

  // 從 map / 深層連結跳卡：設 active 後，逐幀重新對齊到該卡，直到對齊或逾時才清旗標。
  //
  // 為什麼要逐幀重試（而不是單一 rAF）：
  //   單一 rAF 在版面尚未定型時（冷啟動 mock→real 換卡、慢裝置、scroll-snap 重算）
  //   會落在鄰卡、或根本沒捲到 → 停在頂端。此時 IntersectionObserver 觀察到頂端的
  //   第一張卡，把 activeMomentId 覆蓋成第一張 → 表現為「深層連結跳到第一張歌」。
  //   逐幀重新 scrollIntoView 把視窗壓在目標卡上，直到目標真的對齊頂端，IO 才會
  //   確認在目標卡上。
  useEffect(() => {
    if (!pendingPickId) return
    if (!visibleMoments.some((m) => m.id === pendingPickId)) return
    setActiveMomentId(pendingPickId)
    let raf = 0
    let tries = 0
    const settle = () => {
      const el = document.querySelector(`[data-moment-id="${pendingPickId}"]`) as HTMLElement | null
      const feed = document.querySelector('.snap-feed') as HTMLElement | null
      if (el && feed) {
        // 必須 'instant'：.snap-feed 的 CSS 是 scroll-behavior: smooth，'auto' 會沿用它
        // → 平滑捲動 + scroll-snap-stop:always 一次只過一個 snap point，跨不到很遠的卡
        // （如最後一張），在重試視窗內停在鄰卡。'instant' 瞬間跳到位、不被 snap-stop 卡。
        el.scrollIntoView({ block: 'start', behavior: 'instant' })
        const aligned = Math.abs(el.getBoundingClientRect().top - feed.getBoundingClientRect().top) < 8
        if (aligned || ++tries >= 30) {
          setPendingPickId(null)
          return
        }
      } else if (++tries >= 30) {
        setPendingPickId(null)
        return
      }
      raf = requestAnimationFrame(settle) // 約最多 30 幀（~500ms）
    }
    raf = requestAnimationFrame(settle)
    return () => cancelAnimationFrame(raf)
  }, [visibleMoments, pendingPickId])

  const activeMoment = useMemo(
    () => visibleMoments.find((m) => m.id === activeMomentId) ?? visibleMoments[0] ?? null,
    [visibleMoments, activeMomentId]
  )
  const activeCardId = activeMomentId ?? visibleMoments[0]?.id ?? null

  // 全部可切的 slug 順序 — null（全部） → 吸睛度 → 各分類
  const slugs = useMemo<FilterSlug[]>(
    () => [null, HOT_SLUG, ...categories.map((c) => c.slug)],
    [categories]
  )

  // 統一切換函數
  const changeFilter = useCallback(
    (next: FilterSlug) => {
      if (next === activeFilter) return
      const cur = slugs.findIndex((s) => s === activeFilter)
      const nxt = slugs.findIndex((s) => s === next)
      setSlideDir(nxt === -1 || nxt > cur ? 1 : -1)
      setSlideNonce((n) => n + 1)
      setActiveFilter(next)
    },
    [slugs, activeFilter]
  )

  // URL 同步：{ mapOpen, activeFilter } ↔ window.location.pathname
  const urlState = useMemo(() => ({ mapOpen, slug: activeFilter }), [mapOpen, activeFilter])
  const onUrlPop = useCallback(
    (next: { mapOpen: boolean; slug: FilterSlug }) => {
      // 驗證 slug 還在當前可用的 slugs 裡（避免別人手改 URL 到不存在的分類）
      if (next.slug !== null && next.slug !== HOT_SLUG && !slugs.includes(next.slug)) return
      setMapOpen(next.mapOpen)
      changeFilter(next.slug)
    },
    [slugs, changeFilter]
  )
  useCategoryUrl(urlState, onUrlPop)

  // 深層連結 ?m=<id>：等 moments 載入後 → 切到該卡分類 + 由 pendingPickId 接手 scrollIntoView，
  // 然後把 ?m 從網址清掉（一次性，避免之後切分類被拉回）。找不到 id（已刪）→ 停在分類頂端不報錯。
  useEffect(() => {
    if (deepLinkConsumed.current) return
    const raw = new URLSearchParams(window.location.search).get('m')
    // 防禦：分享連結被附加多餘文字時（複製貼上、訊息 App 接字），只取開頭合法 id 片段。
    // Firestore id 為 [A-Za-z0-9]，留 _- 容錯；遇到逗號/中文/空白等就截斷。
    const id = raw?.match(/^[A-Za-z0-9_-]+/)?.[0] ?? null
    if (!id) return
    const target = moments.find((mm) => mm.id === id)
    if (!target) return // 等 moments 載入；真的找不到就讓它停在分類頂端
    deepLinkConsumed.current = true
    setImmersed(true)
    if (target.category?.slug) changeFilter(target.category.slug)
    setPendingPickId(id)
    const url = new URL(window.location.href)
    url.searchParams.delete('m')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  }, [moments, changeFilter])

  // 左右滑 / ←→ 鍵切換分類（Shorts-style）
  useEffect(() => {
    if (!immersed) return
    const cycle = (dir: 1 | -1) => {
      const cur = slugs.findIndex((s) => s === activeFilter)
      const next = (cur + dir + slugs.length) % slugs.length
      setSlideDir(dir)
      setSlideNonce((n) => n + 1)
      setActiveFilter(slugs[next])
    }
    // 鍵盤
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        cycle(1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        cycle(-1)
      }
    }
    // 觸控橫滑 / 觸控板橫向 wheel
    let sx = 0, sy = 0, st = 0
    let lastWheelAt = 0
    const onTouchStart = (e: TouchEvent) => {
      sx = e.touches[0].clientX
      sy = e.touches[0].clientY
      st = Date.now()
    }
    const onTouchEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0]
      const dx = t.clientX - sx
      const dy = t.clientY - sy
      const dt = Date.now() - st
      // 橫向位移 > 40px 且大於縱向 → 認定為橫滑；放寬一點讓手機比較好切分類。
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.1 || dt > 800) return
      cycle(dx < 0 ? 1 : -1)
    }
    const onWheel = (e: WheelEvent) => {
      const now = Date.now()
      if (now - lastWheelAt < 450) return
      if (Math.abs(e.deltaX) < 35 || Math.abs(e.deltaX) < Math.abs(e.deltaY) * 1.2) return
      lastWheelAt = now
      cycle(e.deltaX > 0 ? 1 : -1)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    window.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('wheel', onWheel)
    }
  }, [immersed, activeFilter, slugs])

  // sessionStorage 記得上次 unmute 過 → 等第一個 gesture 自動 unmute
  // Intro 頁也綁（滑動進 feed 就是 gesture），不能用 immersed 當 gate
  // 不能直接初始 muted=false，否則 iframe autoplay 會被 policy 擋（iOS 尤其嚴格）
  useEffect(() => {
    let remembered = false
    try {
      remembered = sessionStorage.getItem(UNMUTE_KEY) === '1'
    } catch {
      /* ignore */
    }
    if (!remembered) return
    const unlock = () => {
      setMuted(false)
      setReplayNonce((n) => n + 1)
    }
    window.addEventListener('click', unlock, { once: true })
    window.addEventListener('touchstart', unlock, { once: true })
    window.addEventListener('wheel', unlock, { once: true, passive: true })
    return () => {
      window.removeEventListener('click', unlock)
      window.removeEventListener('touchstart', unlock)
      window.removeEventListener('wheel', unlock)
    }
  }, [])

  // feedEl 改用 state — AnimatePresence 會 remount <main>，需要重新抓 element
  const [feedEl, setFeedEl] = useState<HTMLElement | null>(null)
  const onFeedRef = useCallback((el: HTMLElement | null) => setFeedEl(el), [])
  // 從 intro 切進 feed 時，壓制 scroll 慣性 — 強制維持在第一張卡 ~500ms
  // 解決「一滑進入後第一張被慣性跳過」的問題。
  // ⚠ 只在「從 intro 進來」時跑：深層連結（?m=）直接進站時 immersed 一開始就是 true，
  //   若也跑這段，會把 pendingPickId 的 scrollIntoView 壓回頂端 → 深層連結定位失效。
  // ⚠ 且一次性消費：enteredViaIntroRef 是永久旗標，若不消費，之後從地圖/策展回 feed
  //   時 feedEl 重掛會再跑一次、把地圖點歌的定位捲動壓回第一首。
  useEffect(() => {
    if (!immersed || !feedEl || !enteredViaIntroRef.current) return
    enteredViaIntroRef.current = false // 消費：只壓制 intro→feed 這一次
    const el = feedEl
    el.scrollTop = 0
    let ticks = 0
    const id = window.setInterval(() => {
      el.scrollTop = 0
      if (++ticks > 30) window.clearInterval(id) // ~500ms @ 16ms
    }, 16)
    return () => window.clearInterval(id)
  }, [immersed, feedEl])

  // 瀏覽器 autoplay policy：muted autoplay 永遠可、unmuted 要該 iframe 自己的
  // user gesture。之前試過「第一次手勢就全域 unmute」會讓 iOS Safari 在後續卡片
  // 被擋下 → 卡片變紅色 ▶ 要點才能播。
  // 改：只信任使用者點擊「解除靜音」按鈕，那個 click 的 gesture scope 含該 iframe。

  // dwell 統計：追蹤 activeMomentId 的停留時間，切換時 / 頁面離開時上報
  const dwellStartRef = useRef<{ momentId: string; t0: number } | null>(null)
  useEffect(() => {
    // 前一個 active 如果有紀錄 → 送出
    const prev = dwellStartRef.current
    if (prev) {
      const ms = Date.now() - prev.t0
      if (ms >= 1000 && !prev.momentId.startsWith('__')) {
        api.reportDwell(prev.momentId, ms)
      }
    }
    dwellStartRef.current = activeMomentId
      ? { momentId: activeMomentId, t0: Date.now() }
      : null
  }, [activeMomentId])

  // 頁面關閉 / 切到背景 — 把當前最後一段 dwell 送出
  useEffect(() => {
    const flush = () => {
      const prev = dwellStartRef.current
      if (!prev) return
      const ms = Date.now() - prev.t0
      if (ms >= 1000 && !prev.momentId.startsWith('__')) {
        api.reportDwell(prev.momentId, ms)
      }
      dwellStartRef.current = null
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    window.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', flush)
      window.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const handleUnmute = () => {
    setMuted(false)
    // 開聲音的當下 → seek 回當前 moment 的 startSeconds，從頭聽一遍那一刻
    setReplayNonce((n) => n + 1)
    try {
      sessionStorage.setItem(UNMUTE_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const handleToggleMute = () => {
    if (muted) {
      handleUnmute()
    } else {
      setMuted(true)
      try {
        sessionStorage.removeItem(UNMUTE_KEY)
      } catch {
        /* ignore */
      }
    }
  }

  // 分享：優先叫系統分享面板（手機 / 支援的桌機瀏覽器）；不支援 → 退回複製連結 modal。
  // 必須同步呼叫 navigator.share，不能在前面 await（會斷掉 user gesture → NotAllowedError）。
  const handleShare = useCallback((m: MomentView | null) => {
    if (!m) return
    const url = buildShareUrl(m)
    const shareData = { title: m.title, text: `${m.title} · 周深 Moment`, url }
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator.share(shareData).catch((err: DOMException) => {
        // 使用者取消（AbortError）→ 什麼都不做；其他錯誤才退回 modal
        if (err?.name !== 'AbortError') setShareMoment(m)
      })
      return
    }
    setShareMoment(m)
  }, [])

  const enterFeed = useCallback(() => {
    if (introExiting) return
    enteredViaIntroRef.current = true
    // 進 feed 先保持靜音，確保瀏覽器允許第一支影片自動播放。
    // 使用者再點卡片上的「解除靜音」即可從該 moment 起點重聽。
    setMuted(true)
    setImmersed(true)
    setIntroExiting(true)
    window.setTimeout(() => setIntroVisible(false), 720)
  }, [introExiting])

  // 從 intro 直接進「總覽地圖」/「音樂時光」（不走 feed 的滑動進場動畫）
  const enterMap = useCallback(() => {
    setImmersed(true)
    setIntroVisible(false)
    setMapOpen(true)
  }, [])
  const enterPlayer = useCallback(() => {
    setImmersed(true)
    setIntroVisible(false)
    setSharedPlayer(null) // 按鈕進入 → 隨機 seed（非分享連結）
    setPlayerOpen(true)
  }, [])

  // 離開播放器：關閉 + 清掉分享連結 query（避免重整又自動開）
  const closePlayer = useCallback(() => {
    setPlayerOpen(false)
    setSharedPlayer(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('play')
    url.searchParams.delete('from')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  }, [])

  // 音樂時光：全螢幕獨立播放器，取代 feed + nav（早退避免兩個 YouTube player 同時播）
  if (immersed && playerOpen) {
    return (
      <Suspense
        fallback={
          <div className="flex h-[100dvh] items-center justify-center bg-ink text-sm text-paper/60">
            載入音樂時光…
          </div>
        }
      >
        <PlayerMode
          moments={moments}
          onClose={closePlayer}
          initialSeed={sharedPlayer?.seed}
          initialFromStart={sharedPlayer?.fromStart}
        />
      </Suspense>
    )
  }

  if (!immersed) {
    return (
      <>
        <Intro
          onStart={enterFeed}
          onBrowseMap={enterMap}
          onPlayer={enterPlayer}
          intro={pickedIntro ?? DEFAULT_INTRO.intros[0]}
          intros={homepageSettings.intros}
          onPickIntro={(i) => setPickedIntro(i)}
          canEdit={canEdit}
          onEditIntro={() => setIntroEditorOpen(true)}
          muted={muted}
          onToggleMute={handleToggleMute}
          exiting={false}
        />
        {introEditorOpen && (
          <Suspense fallback={null}>
            <IntroVideoEditor
              current={homepageSettings}
              onClose={() => setIntroEditorOpen(false)}
              onSaved={() => setReloadKey((k) => k + 1)}
            />
          </Suspense>
        )}
      </>
    )
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-paper">
      <CategoryNav
        categories={categories}
        activeSlug={activeFilter}
        onSelect={changeFilter}
        user={user}
        canEdit={canEdit}
        mode={viewMode}
        onModeChange={setViewMode}
        muted={muted}
        onToggleMute={handleToggleMute}
        onOpenMap={() => setMapOpen(true)}
        onOpenPlayer={() => setPlayerOpen(true)}
        feedActive={!mapOpen && !(viewMode === 'curation' && canEdit)}
        activeMoment={activeMoment}
        paused={paused}
        onTogglePause={() => setPaused((p) => !p)}
        onShare={() => handleShare(activeMoment)}
        onScrollToActive={() => {
          if (!activeMomentId) return
          const el = document.querySelector(`[data-moment-id="${activeMomentId}"]`) as HTMLElement | null
          el?.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }}
      />

      {user && !canEdit && <UnauthorizedBanner user={user} />}

      {viewMode === 'curation' && canEdit ? (
        <main className="flex-1 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-sm text-ink-muted">
                載入策展工具中…
              </div>
            }
          >
            <CurationBoard
              categories={categories}
              moments={moments}
              onMomentClick={(m) => setEditor({ kind: 'edit', moment: m })}
              onReload={() => setReloadKey((k) => k + 1)}
              onChange={setMoments}
              onExit={() => setViewMode('feed')}
              onManageIntro={() => setIntroEditorOpen(true)}
            />
          </Suspense>
        </main>
      ) : mapOpen ? (
        <main className="flex-1 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-sm text-ink-muted">
                載入地圖中…
              </div>
            }
          >
            <MapView
              categories={categories}
              moments={moments}
              onClose={() => setMapOpen(false)}
              onPick={(m, fromSlug) => {
                // 1. 關地圖；2. 切到該 lane 的 filter；3. 標記 pendingPickId 由 effect 接手 scrollIntoView
                setMapOpen(false)
                if (fromSlug !== activeFilter) changeFilter(fromSlug)
                setPendingPickId(m.id)
              }}
            />
          </Suspense>
        </main>
      ) : (
      <div
        className={`relative flex-1 overflow-hidden bg-ink ${
          introVisible && introExiting ? 'feed-enter-from-intro' : ''
        }`}
      >
        {/* 背景層：唯一的 persistent YouTube iframe，activeMoment 變化 → loadVideoById */}
        <ActivePlayer
          moment={activeMoment}
          muted={muted}
          paused={paused}
          replayNonce={replayNonce}
          presentation="feed"
        />

        {/* 前景層：縮圖 / 文字 overlay */}
          <main
            ref={onFeedRef}
            className="snap-feed absolute inset-0"
          >
            <div
              key={`${activeFilter ?? '__all__'}:${slideNonce}`}
              className={`min-h-full feed-slide-${slideDir < 0 ? 'prev' : 'next'}`}
            >
            {visibleMoments.length === 0 ? (
              <div className="flex min-h-[60vh] items-center justify-center text-ink-muted">
                這個分類還沒有策展
              </div>
            ) : (
              visibleMoments.map((m, i) => (
                <MomentCard
                  key={m.id}
                  ref={registerItem(m.id)}
                  moment={m}
                  index={i}
                  isActive={m.id === activeCardId}
                  muted={muted}
                  onUnmute={handleUnmute}
                  showBackdrop={m.id !== activeCardId}
                  canEdit={canEdit && m.id !== SHARED_MOMENT_ID}
                  onEdit={() => setEditor({ kind: 'edit', moment: m })}
                  onShare={m.id !== SHARED_MOMENT_ID ? () => handleShare(m) : undefined}
                />
              ))
            )}
            </div>
          </main>
      </div>
      )}

      {/* FAB：新增 */}
      {canEdit && (
        <button
          onClick={() => setEditor({ kind: 'new' })}
          className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full
                     bg-ink text-2xl text-paper shadow-xl transition hover:bg-ink-soft active:scale-95
                     md:bottom-10 md:right-10"
          aria-label="新增感動點"
        >
          +
        </button>
      )}

      {editor && (
        <Suspense fallback={null}>
          <MomentEditor
            mode={editor}
            categories={categories}
            onClose={() => setEditor(null)}
            onSaved={() => setReloadKey((k) => k + 1)}
          />
        </Suspense>
      )}

      {/* 換首頁背景影片 modal — 策展 banner 的「首頁背景」按鈕會開（immersed 分支也要渲染）*/}
      {introEditorOpen && (
        <Suspense fallback={null}>
          <IntroVideoEditor
            current={homepageSettings}
            onClose={() => setIntroEditorOpen(false)}
            onSaved={() => setReloadKey((k) => k + 1)}
          />
        </Suspense>
      )}

      {/* navigator.share 不支援時的退場：複製連結 modal */}
      {shareMoment && (
        <Suspense fallback={null}>
          <ShareModal moment={shareMoment} onClose={() => setShareMoment(null)} />
        </Suspense>
      )}

      {introVisible && (
        <div className={`fixed inset-0 z-40 ${introExiting ? 'intro-exit-overlay' : ''}`}>
          <Intro
            onStart={enterFeed}
            onBrowseMap={enterMap}
            onPlayer={enterPlayer}
            intro={pickedIntro ?? DEFAULT_INTRO.intros[0]}
            intros={homepageSettings.intros}
            onPickIntro={(i) => setPickedIntro(i)}
            canEdit={canEdit}
            onEditIntro={() => setIntroEditorOpen(true)}
            muted={muted}
            onToggleMute={handleToggleMute}
            exiting={introExiting}
          />
        </div>
      )}
    </div>
  )
}

function UnauthorizedBanner({ user }: { user: User }) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div className="sticky top-[52px] z-20 bg-amber-50 px-4 py-2 text-xs text-amber-900 shadow-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
        <div className="min-w-0">
          ⚠ 已登入 <span className="font-mono">{user.email}</span>，此帳號未在編輯白名單 — 以訪客模式瀏覽。
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={async () => {
              const { loginWithGoogle, logout } = await import('../lib/firebase')
              await logout()
              await loginWithGoogle()
            }}
            className="rounded-full border border-amber-300 bg-paper-card px-3 py-1 hover:bg-paper"
          >
            切換帳號
          </button>
          <button onClick={() => setDismissed(true)} className="text-amber-700 hover:text-amber-900" aria-label="關閉">
            ✕
          </button>
        </div>
      </div>
    </div>
  )
}

function Intro({
  onStart,
  onBrowseMap,
  onPlayer,
  intro,
  intros,
  onPickIntro,
  canEdit,
  onEditIntro,
  muted,
  onToggleMute,
  exiting
}: {
  onStart: () => void
  onBrowseMap: () => void
  onPlayer: () => void
  intro: IntroVideo
  /** 首頁背景輪詢池 — 底部縮圖集錦，點了即時切換底圖 */
  intros: IntroVideo[]
  onPickIntro: (intro: IntroVideo) => void
  canEdit: boolean
  onEditIntro: () => void
  muted: boolean
  onToggleMute: () => void
  exiting?: boolean
}) {
  const videoRef = useRef<IntroBackgroundVideoHandle>(null)
  // stall：autoplay 被擋下（iOS Low Power、Data Saver、PWA standalone 等）→ 露大按鈕
  const [stalled, setStalled] = useState(false)
  // 確認 user 點過 unmute 按鈕了 — 用來決定要不要繼續顯示「點我聽他的聲音」的呼吸提示
  const [hasUserUnmuted, setHasUserUnmuted] = useState(!muted)
  useEffect(() => {
    if (!muted) setHasUserUnmuted(true)
  }, [muted])
  // 輕滑即可進 feed；保留一點累積門檻，避免只是微滾就誤觸。
  useEffect(() => {
    if (exiting) return
    let done = false
    let wheelAccum = 0
    let touchStartY = 0
    const WHEEL_THRESHOLD = 140
    const TOUCH_THRESHOLD = Math.max(90, window.innerHeight / 6)

    const onWheel = (e: WheelEvent) => {
      if (done) return
      if (e.deltaY <= 0) {
        wheelAccum = 0 // 反向滑動歸零
        return
      }
      wheelAccum += e.deltaY
      if (wheelAccum >= WHEEL_THRESHOLD) {
        done = true
        onStart()
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
    }
    const onTouchMove = (e: TouchEvent) => {
      if (done) return
      const dy = touchStartY - e.touches[0].clientY // 手指往上 → 進 feed
      if (dy >= TOUCH_THRESHOLD) {
        done = true
        onStart()
      }
    }

    window.addEventListener('wheel', onWheel, { passive: true })
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [onStart, exiting])

  return (
    <div
      className={`relative min-h-screen overflow-hidden bg-ink text-paper ${
        exiting ? 'pointer-events-none' : ''
      }`}
    >
      <IntroBackgroundVideo
        ref={videoRef}
        youtubeId={intro.youtubeId}
        startSeconds={intro.startSeconds}
        muted={muted}
        onStalled={() => setStalled(true)}
        onPlaying={() => setStalled(false)}
      />

      {/* 頂部：換背景影片（admin）*/}
      {canEdit && (
        <button
          onClick={onEditIntro}
          className="absolute right-4 top-4 z-20 rounded-full border border-paper/20 bg-ink/40 px-4 py-2
                     text-xs text-paper backdrop-blur transition hover:bg-ink/60 md:right-6 md:top-6"
        >
          ✏️ 換背景影片
        </button>
      )}

      {/* 左上：音量鍵 — muted 時放大 + 微微呼吸吸引注意；已開聲音回到小尺寸 */}
      <button
        onClick={onToggleMute}
        className={`intro-unmute-btn absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border
                   backdrop-blur transition md:left-6 md:top-6 ${
                     muted && !hasUserUnmuted
                       ? 'intro-unmute-pulse border-paper/40 bg-paper/15 px-4 py-2.5 text-sm font-medium text-paper shadow-lg hover:bg-paper/25'
                       : 'border-paper/20 bg-ink/40 px-3 py-2 text-xs text-paper hover:bg-ink/60'
                   }`}
        aria-label={muted ? '打開聲音' : '關閉聲音'}
      >
        <span className={muted && !hasUserUnmuted ? 'text-lg leading-none' : 'text-base leading-none'}>
          {muted ? '🔇' : '🔊'}
        </span>
        <span className={muted && !hasUserUnmuted ? 'inline' : 'hidden sm:inline'}>
          {muted ? '點我聽他的聲音' : '聲音開啟'}
        </span>
      </button>

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <p className="mb-6 text-xs tracking-[0.35em] text-paper/70">
          A FAN CURATION · NON-COMMERCIAL
        </p>
        <h1
          className="font-serif text-4xl font-bold leading-tight tracking-tightest text-paper md:text-6xl"
          style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
        >
          從他歌聲裡
          <br />
          最動人的那幾秒開始
        </h1>
        <p
          className="mt-6 max-w-reading text-base text-paper/85 md:text-lg"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.7)' }}
        >
          往下滑，讓每一張卡片自動播放。
          <br className="md:hidden" />
          我們只把感動點攤開，其他交給他的聲音。
        </p>
        {/* 兩個入口：往下滑進時間串之外，也能直接進總覽地圖 / 音樂時光播放器 */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={onBrowseMap}
            className="inline-flex items-center gap-2 rounded-full border border-paper/30 bg-paper/10 px-5 py-2.5 text-sm font-medium text-paper backdrop-blur transition hover:bg-paper/20"
          >
            🗺 總覽地圖
          </button>
          <button
            onClick={onPlayer}
            className="inline-flex items-center gap-2 rounded-full bg-paper px-5 py-2.5 text-sm font-medium text-ink shadow-lg transition hover:bg-paper/90"
          >
            🎵 音樂時光
          </button>
        </div>
        <p className="mt-8 text-xs text-paper/60">非營利粉絲站 · 所有影片來源為 YouTube 官方嵌入</p>
      </div>

      {/* 「往下滑」呼吸提示（點也能直接進）— 位置上移避開手機 home indicator + 視覺中央偏下 */}
      <button
        onClick={onStart}
        className="absolute bottom-[18%] left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2
                   text-paper/70 transition hover:text-paper md:bottom-[14%]"
        aria-label="往下滑進入時間串"
      >
        <span className="text-xs tracking-[0.25em]">直接往下滑</span>
        {/* 兩個箭頭錯時下落，呈現「持續往下流」的視覺 */}
        <span className="relative block h-7 w-6">
          {[0, 0.5].map((delay, i) => (
            <span
              key={i}
              className="intro-arrow absolute inset-x-0 text-center text-xl leading-none"
              style={{ animationDelay: `${delay}s` }}
            >
              ↓
            </span>
          ))}
        </span>
      </button>

      {/* 底部縮圖集錦：點任一張即時切換首頁背景底圖（不只被動輪詢）*/}
      {intros.length > 1 && (
        <div className="absolute inset-x-0 bottom-0 z-10">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/30 to-transparent"
          />
          <div className="relative flex items-center gap-2 overflow-x-auto px-4 pb-3 pt-8 md:justify-center md:px-8">
            <span className="pointer-events-none hidden shrink-0 pr-1 text-[10px] tracking-[0.25em] text-paper/55 md:inline">
              換底圖
            </span>
            {intros.map((it, i) => {
              const active = it.youtubeId === intro.youtubeId
              return (
                <button
                  key={`${it.youtubeId}-${i}`}
                  onClick={() => onPickIntro(it)}
                  className={`relative aspect-video w-16 shrink-0 overflow-hidden rounded-md transition md:w-20 ${
                    active
                      ? 'ring-2 ring-paper'
                      : 'opacity-60 ring-1 ring-paper/25 hover:opacity-100'
                  }`}
                  title={it.note || '切換首頁底圖'}
                  aria-label={`切換首頁底圖：${it.note || it.youtubeId}`}
                  aria-pressed={active}
                >
                  <img
                    src={`https://i.ytimg.com/vi/${it.youtubeId}/mqdefault.jpg`}
                    alt=""
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Stall fallback：iOS Low Power / Data Saver 等情境下 muted autoplay 被擋
          → 露大顆 ▶；onClick 同 stack 內呼叫 videoRef.play() 才能解鎖（iOS 唯一解） */}
      {stalled && (
        <button
          type="button"
          onClick={() => {
            videoRef.current?.play()
            setStalled(false)
          }}
          className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-ink/85 text-paper backdrop-blur-sm"
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-paper/30 bg-paper/10 shadow-2xl">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M6 4 L20 12 L6 20 Z" />
            </svg>
          </div>
          <div className="text-sm font-medium tracking-wider">點此開始觀看</div>
          <div className="max-w-[16rem] text-center text-xs text-paper/60">
            你的瀏覽器擋掉了自動播放（可能是省電模式或限流設定）— 點一下就能繼續
          </div>
        </button>
      )}
    </div>
  )
}
